import { useId, useState } from 'react'
import { Icon, type IconName } from './Icon'
import { useDialog } from '../useDialog'

/** One question the dialog asks before it makes something. */
export interface CreateField {
  key: string
  label: string
  placeholder?: string
  /** The rule the answer has to obey, on the screen while it is being typed. */
  note?: string
  /**
   * A line under the field derived from what is in it, shown while somebody
   * types. The New page dialog uses it for the address a title will produce —
   * the board asked for the address to be visible at creation, and showing it
   * is more honest than a field somebody can edit into a mismatch.
   */
  derived?: (values: Record<string, string>) => string | null
  /** For an answer that is an address rather than a sentence. */
  mono?: boolean
  /**
   * A question with a fixed set of answers — "Place under", which is a walk
   * of the navigation rather than something anybody should type. Given, the
   * field draws a select instead of a box, and `indent` is how deep each
   * choice sits so the list reads as the tree it came from.
   */
  choices?: { value: string; label: string; indent?: number }[]
}

/**
 * The question this product asks before it makes a thing.
 *
 * Six `window.prompt`s stood behind this: naming a docs site, naming a draft,
 * a page's address followed by a second box asking its title, and a version's
 * name followed by a second box asking what readers should call it. A prompt
 * is the operating system interrupting the product. It cannot be styled, it
 * covers whatever it is asking about, it carries no rule, it validates
 * nothing, and a browser driving the page can only dismiss it. On a phone it
 * is a system sheet over an application that has its own.
 *
 * Worse than any of that on the two-box paths: two boxes in sequence is two
 * decisions where the person has one, and answering the first and cancelling
 * the second left them with a page called "New page", or a version whose
 * reader-facing label is its slug.
 *
 * One dialog, on the sheet shell the rest of the product uses, with every
 * question on it at once and the rule under each field while it is being
 * typed. A refusal from the server lands INSIDE it, with what was typed still
 * in the fields — the prompt's answer arrived as a toast after the box had
 * closed and taken the typing with it.
 */
export function CreateDialog({
  title,
  icon,
  what,
  fields,
  initial,
  submitLabel,
  onSubmit,
  onClose,
}: {
  title: string
  icon: IconName
  /** One sentence saying what pressing the button will do. */
  what: string
  fields: CreateField[]
  initial: Record<string, string>
  submitLabel: string
  /** Rejects with the reason, which is drawn in here rather than replacing it. */
  onSubmit: (values: Record<string, string>) => Promise<void>
  onClose: () => void
}) {
  const [values, setValues] = useState(initial)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const panel = useDialog<HTMLDivElement>(true, onClose)
  const id = useId()
  // The first answer is the required one — an address, or a name. Anything
  // after it has a usable default, which is why it is not first.
  const ready = (values[fields[0]!.key] ?? '').trim() !== ''

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!ready || busy) return
    setBusy(true)
    setError(null)
    try {
      await onSubmit(values)
    } catch (e) {
      // Kept open, holding what was typed. A name the server refuses is a name
      // somebody is one character away from fixing.
      setError(e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  return (
    <div className="scrim" onMouseDown={onClose}>
      <div
        className="sheet"
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="sheet__head">
          <h2 className="sheet__title">
            <Icon name={icon} size={17} />
            {title}
          </h2>
          <button className="btn btn--ghost btn--icon" onClick={onClose}>
            <Icon name="close" size={15} label="Close" />
          </button>
        </header>
        <div className="sheet__body">
          <form className="make" onSubmit={(e) => void submit(e)}>
            <p className="make__what">{what}</p>
            {fields.map((field, index) => (
              /*
                The rule is beside the field, not inside its label. A `<label>`
                wrapping both makes the note part of the control's accessible
                NAME — a screen reader then reads the whole paragraph back as
                the field's name every time focus lands on it. `describedby`
                is the relationship that sentence actually has, and it is what
                makes the rule reach somebody who is not looking at it.
              */
              <div className="field" key={field.key}>
                <label className="label" htmlFor={`${id}-${field.key}`}>
                  {field.label}
                </label>
                {field.choices ? (
                  <select
                    id={`${id}-${field.key}`}
                    className="input"
                    value={values[field.key] ?? ''}
                    disabled={busy}
                    {...(field.note ? { 'aria-describedby': `${id}-${field.key}-note` } : {})}
                    {...(index === 0 ? { 'data-autofocus': true } : {})}
                    onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
                  >
                    {field.choices.map((choice) => (
                      <option key={choice.value} value={choice.value}>
                        {/* Indented with figure spaces rather than nbsp: an
                            option's text is all a select can shape, and a
                            screen reader reads the trail beside it anyway. */}
                        {'\u2007\u2007'.repeat(choice.indent ?? 0)}
                        {choice.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={`${id}-${field.key}`}
                    className={`input${field.mono ? ' mono' : ''}`}
                    value={values[field.key] ?? ''}
                    placeholder={field.placeholder ?? ''}
                    disabled={busy}
                    {...(field.note ? { 'aria-describedby': `${id}-${field.key}-note` } : {})}
                    {...(index === 0 ? { 'data-autofocus': true } : {})}
                    onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
                  />
                )}
                {field.derived?.(values) && (
                  <p className="field__note field__note--derived">{field.derived(values)}</p>
                )}
                {field.note && (
                  <p className="field__note" id={`${id}-${field.key}-note`}>
                    {field.note}
                  </p>
                )}
              </div>
            ))}
            {/* The panel-scale treatment every other drawer and card uses for
                a failed request. A fifth way of saying it failed is the defect
                `design-system.test.ts` names by hand. */}
            {error && (
              <p className="panel__error" role="alert">
                {error}
              </p>
            )}
            <div className="make__actions">
              <button type="button" className="btn btn--ghost" onClick={onClose}>
                Cancel
              </button>
              <button className="btn btn--primary" type="submit" disabled={busy || !ready}>
                {busy ? <Icon name="spinner" size={13} /> : null}
                {submitLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
